import invariant from "tiny-invariant";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { LegacyFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import PlatformFilterModel, {
  AreaCondition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import BaseLocationField from "@app/util/fieldModel/impl/BaseLocationField";
import { FieldProperties } from "@app/util/fieldModel/impl/Field";
import { isCombinedPlatformCondition, isLegacyCombinedCondition } from "@app/util/viewModel/assert";
import parseAreaCondition from "@app/util/viewModel/converters/parseAreaCondition";
import { getRegularFieldConditionValue } from "@app/util/viewModel/convertFromPlatformFilterModel";
import { getRegularFieldPlatformConditionValue } from "@app/util/viewModel/convertToPlatformFilterModel";
import { isInAreaCondition } from "@app/util/viewModel/util";

class MapLocationField extends BaseLocationField {
  constructor(data: Omit<FieldProperties, "type">) {
    super({
      features: [
        FieldFeature.ADDRESS,
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.GEO_POINT_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.STANDARD,
      ],
      platformAreaFieldName: "geoPoint.coordinates",
      platformFilterNames: ["area", "geoPoint.coordinates"],
      valueGetter: ["geoPoint", "coordinates"],
      ...data,
    });
  }

  convertToPlatformCondition(filterCondition: LegacyFilterCondition): PlatformFilterCondition {
    if (isLegacyCombinedCondition(filterCondition)) {
      return {}; // combined condition is not supported
    } else {
      if (
        filterCondition.operator === FilterOperator.EMPTY ||
        filterCondition.operator === FilterOperator.NOT_EMPTY
      ) {
        return {
          [this._platformAreaFieldName!]: {
            [filterCondition.operator === FilterOperator.EMPTY
              ? PlatformFilterOperator.EQUALS
              : PlatformFilterOperator.NOT_EQUAL]: null,
          },
        };
      } else {
        return {
          [this._platformFilterNames[0]]: getRegularFieldPlatformConditionValue(
            this,
            filterCondition
          ),
        };
      }
    }
  }

  convertFromPlatformCondition(
    filterCondition: PlatformFilterCondition
  ): LegacyFilterCondition | undefined {
    if (isCombinedPlatformCondition(filterCondition)) {
      return super.convertFromPlatformCondition(filterCondition);
    } else {
      if ("mapped" in filterCondition) {
        return {
          operator: filterCondition.mapped ? FilterOperator.NOT_EMPTY : FilterOperator.EMPTY,
          value: undefined,
        };
      } else if (
        "area" in filterCondition &&
        isInAreaCondition(filterCondition[this.platformFilterNames[0]] as AreaCondition)
      ) {
        return parseAreaCondition(filterCondition[this.platformFilterNames[0]] as AreaCondition);
      }

      if (this._platformAreaFieldName! in filterCondition) {
        const keys = Object.keys(filterCondition[this._platformAreaFieldName!] as object);
        if (!keys.length) {
          return undefined;
        }
        return {
          operator: keys.includes(PlatformFilterOperator.EQUALS)
            ? FilterOperator.EMPTY
            : FilterOperator.NOT_EMPTY,
          value: undefined,
        };
      }

      return getRegularFieldConditionValue(this, filterCondition);
    }
  }

  isPresentInPlatformCondition(condition: Record<string, unknown>): boolean {
    return super.isPresentInPlatformCondition(condition) || "mapped" in condition;
  }

  getFilterConditionForMetaField(
    platformFilterModel: PlatformFilterModel
  ): PlatformFilterCondition {
    if (this.platformFilterNames[0] in platformFilterModel) {
      return super.getFilterConditionForMetaField(platformFilterModel);
    } else if ("mapped" in platformFilterModel) {
      return { mapped: platformFilterModel.mapped };
    }
    invariant(false, `Invalid filter condition for ${this.name} field.`);
  }
}

export default MapLocationField;
